import { useCallback, useRef } from 'react'
import { useConnection } from './ConnectionContext'
import { PhotoIndex } from '@/types/photos'
import {
  SERIAL_CHUNK_SIZE,
  SERIAL_CHUNK_DELAY,
  TEENSY_VENDOR_ID
} from '@/types/serial'

// Setup.json format from device
export interface ESPSetup {
  timezone?: string
  wake_time?: string
  image_fit_mode?: string
  auto_cycle?: boolean
  sleep_start_hour?: number
  sleep_end_hour?: number
  display_mode?: 'calendar' | 'shuffle'  // calendar = show photos for current date, shuffle = random from all
}

// RTC time info from device
export interface DeviceTime {
  timeString: string    // YYYY-MM-DD HH:MM:SS
  timestamp: number     // Unix timestamp
  isSet: boolean        // Whether RTC has been set
}

/**
 * Higher-level serial commands for device communication
 */
export function useSerialCommands() {
  const { sendCommand, onMessage, isConnected, port, downloadBinaryFile } = useConnection()
  const commandLock = useRef(false)

  // Acquire command lock to prevent concurrent commands
  const acquireLock = useCallback(async () => {
    while (commandLock.current) {
      await new Promise(resolve => setTimeout(resolve, 50))
    }
    commandLock.current = true
  }, [])

  const releaseLock = useCallback(() => {
    commandLock.current = false
  }, [])

  /**
   * Get photo index from device
   * Returns the parsed JSON index
   */
  const getIndex = useCallback(async (): Promise<PhotoIndex> => {
    if (!isConnected) throw new Error('Not connected')

    await acquireLock()

    try {
      return await new Promise((resolve, reject) => {
        let jsonBuffer = ''
        let collecting = false
        const timeout = setTimeout(() => {
          cleanup()
          reject(new Error('GET_INDEX timeout'))
        }, 15000)

        const cleanup = () => {
          clearTimeout(timeout)
          unsubscribe()
        }

        const handleMessage = (message: string) => {
          if (message === 'OK:INDEX') {
            collecting = true
            jsonBuffer = ''
          } else if (message === 'END_INDEX') {
            cleanup()
            try {
              const index = JSON.parse(jsonBuffer) as PhotoIndex
              resolve(index)
            } catch (e) {
              reject(new Error(`Failed to parse index JSON: ${e}`))
            }
          } else if (collecting) {
            jsonBuffer += message
          } else if (message.startsWith('ERROR:')) {
            cleanup()
            reject(new Error(message.substring(6)))
          }
        }

        const unsubscribe = onMessage(handleMessage)

        // Send command - ignore its promise, we handle responses manually
        sendCommand('GET_INDEX', 15000).catch(() => {})
      })
    } finally {
      releaseLock()
    }
  }, [isConnected, sendCommand, onMessage, acquireLock, releaseLock])

  /**
   * Check if connected device is a Teensy 4.1 (for high-speed uploads)
   */
  const isTeensy = useCallback((): boolean => {
    if (!port) return false
    const info = port.getInfo()
    return info.usbVendorId === TEENSY_VENDOR_ID
  }, [port])

  /**
   * Upload a BMP file using high-speed protocol (Teensy 4.1 optimized)
   * Uses flow control: SEND:N requests, 16KB chunks, minimal overhead
   */
  const uploadFileFast = useCallback(async (
    filename: string,
    data: Uint8Array,
    onProgress?: (progress: number) => void
  ): Promise<{ speed: number }> => {
    if (!isConnected || !port) throw new Error('Not connected')

    await acquireLock()

    try {
      onProgress?.(0)
      console.log(`[UploadFast] Starting high-speed upload of ${filename} (${data.length} bytes)`)

      const rawWriter = port.writable?.getWriter()
      if (!rawWriter) {
        throw new Error('Could not get raw writer from port')
      }

      let bytesSent = 0
      let transferSpeed = 0

      try {
        // Send UPLOAD_FAST command
        const uploadCmd = new TextEncoder().encode(`UPLOAD_FAST /${filename} ${data.length}\n`)
        console.log('[UploadFast] Sending UPLOAD_FAST command...')
        await rawWriter.write(uploadCmd)

        // Wait for OK:READY and then handle SEND: requests
        await new Promise<void>((resolve, reject) => {
          const timeout = setTimeout(() => {
            cleanup()
            reject(new Error('Upload timeout'))
          }, 180000) // 3 minute total timeout

          const cleanup = () => {
            clearTimeout(timeout)
            unsubscribe()
          }

          const handleMessage = async (message: string) => {
            // console.log(`[UploadFast] Received: ${message}`)

            if (message === 'OK:READY') {
              console.log('[UploadFast] Device ready, waiting for SEND requests...')
              onProgress?.(2)
            } else if (message.startsWith('SEND:')) {
              // Device requesting N bytes
              const bytesToSend = parseInt(message.substring(5), 10)
              if (isNaN(bytesToSend) || bytesToSend <= 0) {
                console.warn('[UploadFast] Invalid SEND request:', message)
                return
              }

              // Send the requested bytes
              const chunk = data.slice(bytesSent, bytesSent + bytesToSend)
              await rawWriter.write(chunk)
              bytesSent += chunk.length

              // Update progress (2-95% during transfer)
              const progress = 2 + Math.floor((bytesSent / data.length) * 93)
              onProgress?.(progress)

            } else if (message.startsWith('PROGRESS:')) {
              // Firmware progress update (every 64KB)
              const percent = parseInt(message.substring(9), 10)
              console.log(`[UploadFast] Device progress: ${percent}%`)
            } else if (message.startsWith('SPEED:')) {
              // Extract transfer speed
              const speedMatch = message.match(/SPEED:(\d+\.?\d*)/);
              if (speedMatch) {
                transferSpeed = parseFloat(speedMatch[1])
                console.log(`[UploadFast] Transfer speed: ${transferSpeed} KB/sec`)
              }
            } else if (message.includes('OK:COMPLETE')) {
              console.log('[UploadFast] Upload complete!')
              onProgress?.(100)
              cleanup()
              resolve()
            } else if (message.includes('ERROR:')) {
              cleanup()
              const errorStart = message.indexOf('ERROR:')
              reject(new Error(message.substring(errorStart + 6)))
            }
          }

          const unsubscribe = onMessage(handleMessage)
        })

        // Release writer
        rawWriter.releaseLock()

        return { speed: transferSpeed }

      } catch (err) {
        try { rawWriter.releaseLock() } catch {}
        throw err
      }

    } finally {
      releaseLock()
    }
  }, [isConnected, port, onMessage, acquireLock, releaseLock])

  /**
   * Upload a BMP file to the device (auto-selects best method)
   * Uses high-speed UPLOAD_FAST for Teensy, falls back to legacy UPLOAD for other devices
   */
  const uploadFile = useCallback(async (
    filename: string,
    data: Uint8Array,
    onProgress?: (progress: number) => void
  ): Promise<void> => {
    if (!isConnected || !port) throw new Error('Not connected')

    // Use high-speed upload for Teensy 4.1
    if (isTeensy()) {
      console.log('[Upload] Detected Teensy - using high-speed upload')
      const result = await uploadFileFast(filename, data, onProgress)
      console.log(`[Upload] Completed at ${result.speed} KB/sec`)
      return
    }

    // Legacy upload for other devices
    console.log('[Upload] Using legacy upload method')
    await acquireLock()

    try {
      onProgress?.(0)
      console.log(`[Upload] Starting upload of ${filename} (${data.length} bytes)`)

      // Get raw writer - bypass the text encoder for binary data
      const rawWriter = port.writable?.getWriter()
      if (!rawWriter) {
        throw new Error('Could not get raw writer from port')
      }

      try {
        // Send UPLOAD command as raw bytes (matching web v1)
        const uploadCmd = new TextEncoder().encode(`UPLOAD /${filename} ${data.length}\n`)
        console.log('[Upload] Sending UPLOAD command...')
        await rawWriter.write(uploadCmd)

        // Wait 1 second for device to be ready
        console.log('[Upload] Waiting 1s for device to be ready...')
        await new Promise(resolve => setTimeout(resolve, 1000))

        // Send data in chunks (128 bytes, 5ms delay - for legacy devices)
        console.log(`[Upload] Sending ${data.length} bytes in ${SERIAL_CHUNK_SIZE}-byte chunks...`)
        onProgress?.(5)

        for (let i = 0; i < data.length; i += SERIAL_CHUNK_SIZE) {
          const chunk = data.slice(i, Math.min(i + SERIAL_CHUNK_SIZE, data.length))
          await rawWriter.write(chunk)
          if (i + SERIAL_CHUNK_SIZE < data.length) {
            await new Promise(resolve => setTimeout(resolve, SERIAL_CHUNK_DELAY))
          }
          // Update progress (5-50% during send)
          if (i % 8192 === 0) {
            onProgress?.(5 + Math.floor((i / data.length) * 45))
          }
        }

        console.log('[Upload] Data sent, waiting for ACK...')
        onProgress?.(50)

        // Release raw writer so we can receive messages
        rawWriter.releaseLock()

        // Wait for ACK and OK:COMPLETE
        await new Promise<void>((resolve, reject) => {
          const timeout = setTimeout(() => {
            cleanup()
            reject(new Error('Upload timeout - device did not confirm completion'))
          }, 60000)

          const cleanup = () => {
            clearTimeout(timeout)
            unsubscribe()
          }

          const handleMessage = (message: string) => {
            console.log(`[Upload] Received: ${message}`)
            if (message.includes('OK:COMPLETE')) {
              cleanup()
              resolve()
            } else if (message.includes('ACK') && !message.includes('OK:COMPLETE')) {
              console.log('[Upload] ACK received, waiting for OK:COMPLETE...')
              onProgress?.(75)
            } else if (message.includes('ERROR:')) {
              cleanup()
              const errorStart = message.indexOf('ERROR:')
              reject(new Error(message.substring(errorStart + 6)))
            }
          }

          const unsubscribe = onMessage(handleMessage)
        })

        console.log('[Upload] Upload complete!')
        onProgress?.(100)

      } catch (err) {
        // Make sure to release the writer on error
        try { rawWriter.releaseLock() } catch {}
        throw err
      }

    } finally {
      releaseLock()
    }
  }, [isConnected, port, onMessage, acquireLock, releaseLock, isTeensy, uploadFileFast])

  /**
   * Delete a file from the device
   */
  const deleteFile = useCallback(async (filename: string): Promise<void> => {
    if (!isConnected) throw new Error('Not connected')

    await acquireLock()
    try {
      await sendCommand(`DELETE ${filename}`)
    } finally {
      releaseLock()
    }
  }, [isConnected, sendCommand, acquireLock, releaseLock])

  /**
   * List files on the device SD card
   */
  const listFiles = useCallback(async (): Promise<string[]> => {
    if (!isConnected) throw new Error('Not connected')

    await acquireLock()
    try {
      const files: string[] = []

      return await new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          cleanup()
          reject(new Error('LIST timeout'))
        }, 10000)

        const cleanup = () => {
          clearTimeout(timeout)
          unsubscribe()
        }

        let collecting = false

        const handleMessage = (message: string) => {
          if (message === 'OK:LIST') {
            collecting = true
          } else if (message === 'END_LIST') {
            cleanup()
            resolve(files)
          } else if (collecting && message.startsWith('/')) {
            // Format: /filename (size bytes)
            files.push(message)
          } else if (message.startsWith('ERROR:')) {
            cleanup()
            reject(new Error(message.substring(6)))
          }
          // Skip "Files:" header line
        }

        const unsubscribe = onMessage(handleMessage)
        sendCommand('LIST', 10000).catch(() => {})
      })
    } finally {
      releaseLock()
    }
  }, [isConnected, sendCommand, onMessage, acquireLock, releaseLock])

  /**
   * Set current photo for a date
   */
  const setCurrentPhoto = useCallback(async (date: string, index: number): Promise<void> => {
    if (!isConnected) throw new Error('Not connected')

    await acquireLock()
    try {
      await sendCommand(`SET_CURRENT ${date} ${index}`)
    } finally {
      releaseLock()
    }
  }, [isConnected, sendCommand, acquireLock, releaseLock])

  /**
   * Toggle photo enabled/disabled
   */
  const togglePhoto = useCallback(async (date: string, index: number): Promise<void> => {
    if (!isConnected) throw new Error('Not connected')

    await acquireLock()
    try {
      await sendCommand(`TOGGLE ${date} ${index}`)
    } finally {
      releaseLock()
    }
  }, [isConnected, sendCommand, acquireLock, releaseLock])

  /**
   * Rebuild photo index from SD card files
   */
  const rebuildIndex = useCallback(async (): Promise<void> => {
    if (!isConnected) throw new Error('Not connected')

    await acquireLock()
    try {
      await sendCommand('BUILD_INDEX', 30000)
    } finally {
      releaseLock()
    }
  }, [isConnected, sendCommand, acquireLock, releaseLock])

  /**
   * Display a specific file on the e-ink screen
   */
  const displayFile = useCallback(async (filename: string): Promise<void> => {
    if (!isConnected) throw new Error('Not connected')

    await acquireLock()
    try {
      await sendCommand(`DISPLAY ${filename}`, 30000)
    } finally {
      releaseLock()
    }
  }, [isConnected, sendCommand, acquireLock, releaseLock])

  /**
   * Get setup.json from device
   */
  const getSetup = useCallback(async (): Promise<ESPSetup> => {
    if (!isConnected) throw new Error('Not connected')

    await acquireLock()

    try {
      return await new Promise((resolve, reject) => {
        let jsonBuffer = ''
        let collecting = false
        const timeout = setTimeout(() => {
          cleanup()
          reject(new Error('GET_SETUP timeout'))
        }, 15000)

        const cleanup = () => {
          clearTimeout(timeout)
          unsubscribe()
        }

        const handleMessage = (message: string) => {
          if (message === 'OK:SETUP') {
            collecting = true
            jsonBuffer = ''
          } else if (message === 'END_SETUP') {
            cleanup()
            try {
              const setup = JSON.parse(jsonBuffer) as ESPSetup
              resolve(setup)
            } catch (e) {
              reject(new Error(`Failed to parse setup JSON: ${e}`))
            }
          } else if (collecting) {
            jsonBuffer += message
          } else if (message.startsWith('ERROR:')) {
            cleanup()
            reject(new Error(message.substring(6)))
          }
        }

        const unsubscribe = onMessage(handleMessage)
        sendCommand('GET_SETUP', 15000).catch(() => {})
      })
    } finally {
      releaseLock()
    }
  }, [isConnected, sendCommand, onMessage, acquireLock, releaseLock])

  /**
   * Save setup.json to device
   */
  const setSetup = useCallback(async (setup: ESPSetup): Promise<void> => {
    if (!isConnected) throw new Error('Not connected')

    await acquireLock()

    try {
      const json = JSON.stringify(setup)
      // Device expects: SET_SETUP <json> (all on one line)
      await sendCommand(`SET_SETUP ${json}`, 10000)
    } finally {
      releaseLock()
    }
  }, [isConnected, sendCommand, acquireLock, releaseLock])

  /**
   * Get RTC time from device
   */
  const getTime = useCallback(async (): Promise<DeviceTime> => {
    if (!isConnected) throw new Error('Not connected')

    await acquireLock()

    try {
      return await new Promise((resolve, reject) => {
        let timeString = ''
        let timestamp = 0
        let isSet = false
        let collecting = false
        const timeout = setTimeout(() => {
          cleanup()
          reject(new Error('GET_TIME timeout'))
        }, 10000)

        const cleanup = () => {
          clearTimeout(timeout)
          unsubscribe()
        }

        const handleMessage = (message: string) => {
          if (message === 'OK:TIME') {
            collecting = true
          } else if (message === 'END_TIME') {
            cleanup()
            resolve({ timeString, timestamp, isSet })
          } else if (collecting) {
            if (message.startsWith('TIMESTAMP:')) {
              timestamp = parseInt(message.substring(10), 10)
            } else if (message.startsWith('RTC_SET:')) {
              isSet = message.substring(8) === 'true'
            } else if (message.match(/^\d{4}-\d{2}-\d{2}/)) {
              timeString = message
            }
          } else if (message.startsWith('ERROR:')) {
            cleanup()
            reject(new Error(message.substring(6)))
          }
        }

        const unsubscribe = onMessage(handleMessage)
        sendCommand('GET_TIME', 10000).catch(() => {})
      })
    } finally {
      releaseLock()
    }
  }, [isConnected, sendCommand, onMessage, acquireLock, releaseLock])

  /**
   * Set RTC time on device
   * @param datetime - Either Unix timestamp or YYYY-MM-DD HH:MM:SS format
   */
  const setTime = useCallback(async (datetime: string | number): Promise<void> => {
    if (!isConnected) throw new Error('Not connected')

    await acquireLock()

    try {
      const timeStr = typeof datetime === 'number' ? datetime.toString() : datetime
      await sendCommand(`SET_TIME ${timeStr}`, 10000)
    } finally {
      releaseLock()
    }
  }, [isConnected, sendCommand, acquireLock, releaseLock])

  /**
   * Sync device time to browser's current time
   */
  const syncTimeToBrowser = useCallback(async (): Promise<void> => {
    if (!isConnected) throw new Error('Not connected')

    const now = Math.floor(Date.now() / 1000)
    await setTime(now)
  }, [isConnected, setTime])

  /**
   * Download a file from the device
   * Returns the file data as an ArrayBuffer
   */
  const downloadFile = useCallback(async (
    filename: string,
    onProgress?: (progress: number) => void
  ): Promise<ArrayBuffer> => {
    if (!isConnected) throw new Error('Not connected')

    await acquireLock()
    try {
      return await downloadBinaryFile(filename, onProgress)
    } finally {
      releaseLock()
    }
  }, [isConnected, downloadBinaryFile, acquireLock, releaseLock])

  /**
   * Get photo metadata from device
   */
  const getMetadata = useCallback(async (): Promise<Record<string, { baseUrl: string; source: string }>> => {
    if (!isConnected) throw new Error('Not connected')

    await acquireLock()

    try {
      return await new Promise((resolve, reject) => {
        let jsonBuffer = ''
        let collecting = false
        const timeout = setTimeout(() => {
          cleanup()
          reject(new Error('GET_METADATA timeout'))
        }, 15000)

        const cleanup = () => {
          clearTimeout(timeout)
          unsubscribe()
        }

        const handleMessage = (message: string) => {
          if (message === 'OK:METADATA') {
            collecting = true
            jsonBuffer = ''
          } else if (message === 'END_METADATA') {
            cleanup()
            try {
              const metadata = JSON.parse(jsonBuffer || '{}')
              resolve(metadata)
            } catch (e) {
              reject(new Error(`Failed to parse metadata JSON: ${e}`))
            }
          } else if (collecting) {
            jsonBuffer += message
          } else if (message.startsWith('ERROR:')) {
            cleanup()
            reject(new Error(message.substring(6)))
          }
        }

        const unsubscribe = onMessage(handleMessage)
        sendCommand('GET_METADATA', 15000).catch(() => {})
      })
    } finally {
      releaseLock()
    }
  }, [isConnected, sendCommand, onMessage, acquireLock, releaseLock])

  /**
   * Set photo index on device (for restore)
   */
  const setIndex = useCallback(async (index: PhotoIndex): Promise<void> => {
    if (!isConnected) throw new Error('Not connected')

    await acquireLock()

    try {
      const json = JSON.stringify(index)
      await sendCommand(`SET_INDEX ${json}`, 30000)
    } finally {
      releaseLock()
    }
  }, [isConnected, sendCommand, acquireLock, releaseLock])

  /**
   * Set photo metadata on device (for restore)
   */
  const setMetadata = useCallback(async (metadata: Record<string, { baseUrl: string; source: string }>): Promise<void> => {
    if (!isConnected) throw new Error('Not connected')

    await acquireLock()

    try {
      const json = JSON.stringify(metadata)
      await sendCommand(`SET_METADATA_FULL ${json}`, 30000)
    } finally {
      releaseLock()
    }
  }, [isConnected, sendCommand, acquireLock, releaseLock])

  return {
    getIndex,
    uploadFile,
    uploadFileFast,
    downloadFile,
    isTeensy,
    deleteFile,
    listFiles,
    setCurrentPhoto,
    togglePhoto,
    rebuildIndex,
    displayFile,
    getSetup,
    setSetup,
    getTime,
    setTime,
    syncTimeToBrowser,
    getMetadata,
    setIndex,
    setMetadata,
  }
}
